import { chunk } from "lodash";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import { usePassengersPhonesInUse } from "@ryde/redux";
import {
  Log,
  type IDynamicObject,
  type IPassengerPhoneInUseItemDto,
  type RydeUiUnit,
} from "@ryde/shared";

import {
  GENERIC_UPLOAD_PASSENGER_ENTITY,
  PHONES_IN_USE_REQUEST_CHUNK_SIZE,
} from "../generic-upload.constants";
import type { IGenericUploadPhoneConflict } from "../generic-upload.models";
import {
  alignPhonesInUseItems,
  buildPhoneConflicts,
  collectUploadPhones,
  countUnresolvedPhoneConflictRows,
  toggleSkippedDataIndex,
} from "../helpers";

export const useGenericUploadPhoneConflicts = (entity: RydeUiUnit) => {
  const { t } = useTranslation();
  const { fetchPhonesInUse } = usePassengersPhonesInUse();

  const [phoneConflicts, setPhoneConflicts] = useState<
    IGenericUploadPhoneConflict[]
  >([]);
  const [skippedDataIndexes, setSkippedDataIndexes] = useState<number[]>([]);
  const [isExistingPhoneCheckFailed, setIsExistingPhoneCheckFailed] =
    useState(false);
  const [isCheckingPhones, setIsCheckingPhones] = useState(false);

  const resetPhoneConflicts = useEvent((): void => {
    setPhoneConflicts([]);
    setSkippedDataIndexes([]);
    setIsExistingPhoneCheckFailed(false);
  });

  const checkPhoneConflicts = useEvent(
    async (data: IDynamicObject[]): Promise<void> => {
      resetPhoneConflicts();

      if (entity !== GENERIC_UPLOAD_PASSENGER_ENTITY || !data.length) {
        return;
      }

      const phones = collectUploadPhones(data);

      if (!phones.some(Boolean)) {
        return;
      }

      setIsCheckingPhones(true);

      try {
        const items: IPassengerPhoneInUseItemDto[] = [];
        let hasFailedRequest = false;

        for (const phonesChunk of chunk(
          phones,
          PHONES_IN_USE_REQUEST_CHUNK_SIZE,
        )) {
          const result = await fetchPhonesInUse({ phones: phonesChunk });

          if (!result.success || !result.data) {
            hasFailedRequest = true;
            break;
          }

          items.push(...result.data.items);
        }

        // A response that cannot be attributed to rows is as useless as no response.
        const alignedItems = hasFailedRequest
          ? null
          : alignPhonesInUseItems({ phones, items });
        const isCheckFailed = alignedItems === null;

        if (isCheckFailed) {
          Log.warnAndToast(t("genericUpload.phoneConflicts.checkFailed"));
        }

        setIsExistingPhoneCheckFailed(isCheckFailed);
        setPhoneConflicts(buildPhoneConflicts({ data, items: alignedItems }));
      } finally {
        setIsCheckingPhones(false);
      }
    },
  );

  const toggleSkippedRow = useEvent((dataIndex: number): void => {
    setSkippedDataIndexes(current =>
      toggleSkippedDataIndex(current, dataIndex),
    );
  });

  const unresolvedPhoneConflictRowsCount = useMemo(
    () => countUnresolvedPhoneConflictRows(phoneConflicts, skippedDataIndexes),
    [phoneConflicts, skippedDataIndexes],
  );

  return {
    phoneConflicts,
    skippedDataIndexes,
    isExistingPhoneCheckFailed,
    isCheckingPhones,
    unresolvedPhoneConflictRowsCount,
    checkPhoneConflicts,
    toggleSkippedRow,
  };
};
